import { useState, useCallback, useEffect } from 'react';
import {
  isAdmin,
  listUsers,
  assignRoleToUser,
  removeRoleFromUser,
  getUserRoles,
  grantRolePermission,
  revokeRolePermission,
  getRolePermissions,
  canUser as checkPermission,
  getRoles,
  getResources,
  getActions,
} from '../auth/accessControl';
import { useAuthContext } from '../auth/AuthProvider';

export interface User {
  user_id: string;
  email: string;
  created_at: string;
}

export interface Role {
  id: string;
  name: string;
  description?: string;
}

export interface Resource {
  id: string;
  code: string;
  name: string;
  description?: string;
}

export interface Action {
  id: string;
  code: string;
  name: string;
  description?: string;
}

export interface Permission {
  resource: string;
  action: string;
}

export const useAccessControl = () => {
  const { user } = useAuthContext();
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [actions, setActions] = useState<Action[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Check if current user is admin
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        // First try the RPC function
        const admin = await isAdmin();
        setIsAdminUser(admin);
      } catch (err) {
        // Fallback: check user role from auth context
        console.warn('RPC admin check failed, using fallback:', err);
        const isAdminRole = user?.role === 'admin' || user?.role === 'super_admin';
        setIsAdminUser(isAdminRole);
        console.log('Admin status (fallback):', isAdminRole, 'User role:', user?.role);
      }
    };

    if (user) {
      checkAdmin();
    } else {
      setIsAdminUser(false);
    }
  }, [user]);

  // Load reference data
  const loadReferenceData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [rolesData, resourcesData, actionsData] = await Promise.all([
        getRoles(),
        getResources(),
        getActions(),
      ]);
      setRoles(rolesData);
      setResources(resourcesData);
      setActions(actionsData);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load reference data');
      setError(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load users (admin only)
  const loadUsers = useCallback(async () => {
    if (!isAdminUser) {
      setError(new Error('Not authorized'));
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const usersData = await listUsers();
      setUsers(usersData);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load users');
      setError(error);
    } finally {
      setIsLoading(false);
    }
  }, [isAdminUser]);

  // Role management
  const assignRole = useCallback(
    async (userId: string, roleName: string) => {
      try {
        await assignRoleToUser(userId, roleName);
        // Reload users
        await loadUsers();
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to assign role');
        setError(error);
        throw error;
      }
    },
    [loadUsers]
  );

  const removeRole = useCallback(
    async (userId: string, roleName: string) => {
      try {
        await removeRoleFromUser(userId, roleName);
        // Reload users
        await loadUsers();
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to remove role');
        setError(error);
        throw error;
      }
    },
    [loadUsers]
  );

  const fetchUserRoles = useCallback(async (userId: string) => {
    try {
      return await getUserRoles(userId);
    } catch (err) {
      console.error('Error fetching user roles:', err);
      return [];
    }
  }, []);

  // Permission management
  const grantPermission = useCallback(
    async (roleName: string, resourceCode: string, actionCode: string) => {
      try {
        await grantRolePermission(roleName, resourceCode, actionCode);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to grant permission');
        setError(error);
        throw error;
      }
    },
    []
  );

  const revokePermission = useCallback(
    async (roleName: string, resourceCode: string, actionCode: string) => {
      try {
        await revokeRolePermission(roleName, resourceCode, actionCode);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to revoke permission');
        setError(error);
        throw error;
      }
    },
    []
  );

  const fetchRolePermissions = useCallback(async (roleName: string) => {
    try {
      return await getRolePermissions(roleName);
    } catch (err) {
      console.error('Error fetching role permissions:', err);
      return [];
    }
  }, []);

  // Permission check
  const canUserPerformAction = useCallback(
    async (userId: string, resourceCode: string, actionCode: string): Promise<boolean> => {
      try {
        return await checkPermission(userId, resourceCode, actionCode);
      } catch (err) {
        console.error('Error checking permission:', err);
        return false;
      }
    },
    []
  );

  return {
    // State
    isAdmin: isAdminUser,
    user,
    users,
    roles,
    resources,
    actions,
    isLoading,
    error,

    // Methods
    loadReferenceData,
    loadUsers,
    assignRole,
    removeRole,
    fetchUserRoles,
    grantPermission,
    revokePermission,
    fetchRolePermissions,
    canUserPerformAction,
  };
};
